import React, { useEffect, useState } from "react";
import {
    fetchUnfinishedGames, updateGameResults, updateGameStartTime,
    fetchTournamentsAll, updateTournamentStatus, fetchLinks, updateLinkStatus,
    fetchFinishedGames, updateGameChances,
    fetchSeasons, createSeason, updateSeasonStatus
} from "../api/api.js";

const SuperAdmin = () => {
    const [games, setGames] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState("seasons"); // "seasons", "tournaments", "results", "fix-wrong-results", "time", "links", "chances", "old-chances"
    const [formData, setFormData] = useState({}); // {gameId: {...}}
    const [tournaments, setTournaments] = useState(null);
    const [tournamentStatuses, setTournamentStatuses] = useState([]);
    const [linkStatuses, setLinkStatuses] = useState([]);
    const [links, setLinks] = useState(null);
    const [successStatus, setSuccessStatus] = useState({});
    const [seasons, setSeasons] = useState(null);
    const [newSeasonName, setNewSeasonName] = useState("");
    const [newSeasonStatus, setNewSeasonStatus] = useState("pending");

    const seasonStatuses = [
        "pending",
        "live",
        "finished"
    ];

    useEffect(() => {
        if (mode === "seasons") {
            loadSeasons();
        } else if (mode === "tournaments") {
            loadTournaments();
        } else if (mode === "links") {
            loadLinks();
        } else if (mode === "fix-wrong-results" || mode === "old-chances") {
            loadFinishedGames();
        } else {
            loadGames()
        }
    }, [mode]);

    const loadSeasons = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchSeasons();

            setSeasons(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadTournaments = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchTournamentsAll();
            setTournaments(response.data.tournaments);
            setTournamentStatuses(response.data.statuses);
        } catch (err) {
            setError(err.message);
            if (err.message === 'Forbidden') {
                window.location.href = "/predict";
            }
        } finally {
            setLoading(false);
        }
    };

    const loadGames = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchUnfinishedGames();
            setGames(response.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadFinishedGames = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchFinishedGames();
            setGames(response.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadLinks = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchLinks();
            setLinks(response.data.links);
            setLinkStatuses(response.data.statuses);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    const setSuccess = (id, value) => {
        setSuccessStatus(prev => ({ ...prev, [id]: value }));
    };

    const handleSeasonStatusChange = (
        id,
        newStatus
    ) => {
        setFormData(prev => ({
            ...prev,
            [id]: {
                status: newStatus
            }
        }));
    };

    const handleResultChange = (gameId, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [gameId]: {
                ...prev[gameId],
                [field]: value
            },
        }));
    };

    const handleTimeChange = (gameId, value) => {
        setFormData((prev) => ({
            ...prev,
            [gameId]: { ...prev[gameId], start_time: value },
        }));
    };

    const handleTournamentStatusChange = (id, newStatus) => {
        setFormData(prev => ({
            ...prev,
            [id]: { status: newStatus }
        }));
    };

    const handleLinkStatusChange = (id, newStatus) => {
        setFormData(prev => ({
            ...prev,
            [id]: { status: newStatus }
        }));
    };

    const changeSeasonStatus = async (id) => {
        const data = formData[id];

        if (!data || !data.status) {
            alert("Please pick a new status");
            return;
        }

        try {
            await updateSeasonStatus(
                id,
                data.status
            );

            setSuccess(id, "success");
        } catch (error) {
            setSuccess(id, "error");
        }
    };

    const changeTournamentStatus = async (id) => {
        const data = formData[id];
        if (!data || !data.status) {
            alert("Please pick a new status");
            return;
        }

        if (data.status !== 'abandoned') {
            alert('At this moment, it\'s only possible to abandon a tournament')
            return;
        }

        try {
            await updateTournamentStatus(id, data.status)

            setSuccess(id, "success")
        } catch (error) {
            setSuccess(id, "error")
        }
    };

    const changeLinkStatus = async (id) => {
        const data = formData[id];
        if (!data || !data.status) {
            alert("Please pick a new status");
            return;
        }

        try {
            await updateLinkStatus(id, data.status)

            setSuccess(id, "success")
        } catch (error) {
            setSuccess(id, "error")
        }
    };

    const finishGame = async (gameId, fix = null) => {
        const data = formData[gameId];
        if (!data || !data.h || !data.hht || !data.a || !data.aht) {
            alert("Please fill all result fields");
            return;
        }
        try {
            await updateGameResults(gameId, data.h, data.a, data.hht, data.aht, fix)

            setSuccess(gameId, "success")
        } catch (error) {
            setSuccess(gameId, "error")
        }
    };

    const changeStartTime = async (gameId) => {
        const data = formData[gameId];
        if (!data || !data.start_time) {
            alert("Please select new start time");
            return;
        }
        try {
            await updateGameStartTime(gameId, formatDateTimeForApi(data.start_time));

            setSuccess(gameId, "success")
        } catch (error) {
            setSuccess(gameId, "error")
        }
    };

    const addSeason = async () => {
        if (!newSeasonName.trim()) {
            alert("Please enter season name");
            return;
        }

        try {
            await createSeason(
                newSeasonName,
                newSeasonStatus
            );

            setNewSeasonName("");
            setNewSeasonStatus("pending");

            loadSeasons();
        } catch (error) {
            alert(error.message);
        }
    };

    const updateChances = async (gameId) => {
        const data = formData[gameId];
        console.log(data)
        if (
            !data ||
            !data.chance1 ||
            !data.chanceX ||
            !data.chance2
        ) {
            alert("Please fill all chances fields");
            return;
        }
        try {
            await updateGameChances(
                gameId,
                data.chance1,
                data.chanceX,
                data.chance2
            )

            setSuccess(gameId, "success")
        } catch (error) {
            setSuccess(gameId, "error")
        }
    };

    function formatDateTimeForApi(value) {
        if (!value) return null;
        // "2025-09-26T20:00" → "2025-09-26 20:00:00"
        return value.replace("T", " ") + ":00";
    }

    const getChanceValue = (game, key, fieldName) => {
        if (formData[game.id]?.[fieldName] !== undefined) {
            return formData[game.id][fieldName];
        }

        return game.chances?.decimal?.[key] ?? "";
    };

    const renderSeasons = () => {
        return (
            <>
                <div
                    style={{
                        marginBottom: "20px",
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}
                >
                    <input
                        type="text"
                        placeholder="Season name"
                        value={newSeasonName}
                        onChange={(e) =>
                            setNewSeasonName(
                                e.target.value
                            )
                        }
                    />

                    <select
                        value={newSeasonStatus}
                        onChange={(e) =>
                            setNewSeasonStatus(
                                e.target.value
                            )
                        }
                    >
                        {seasonStatuses.map(status => (
                            <option
                                key={status}
                                value={status}
                            >
                                {status}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={addSeason}
                    >
                        Add Season
                    </button>
                </div>

                {(!seasons || seasons.length === 0) ? (

                    <p>No seasons found</p>

                ) : (

                    <table className="update-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Current Status</th>
                                <th>New Status</th>
                                <th>Action</th>
                                <th>Success</th>
                            </tr>
                        </thead>

                        <tbody>
                            {seasons.map(s => (
                                <tr key={s.id}>

                                    <td>
                                        {s.id}
                                    </td>

                                    <td>
                                        {s.name}
                                    </td>

                                    <td>
                                        {s.status}
                                    </td>

                                    <td>
                                        <select
                                            value={
                                                formData[s.id]?.status || ""
                                            }
                                            onChange={(e) =>
                                                handleSeasonStatusChange(
                                                    s.id,
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">
                                                Select...
                                            </option>

                                            {seasonStatuses.map(
                                                status => (
                                                    <option
                                                        key={status}
                                                        value={status}
                                                    >
                                                        {status}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </td>

                                    <td>
                                        <button
                                            onClick={() =>
                                                changeSeasonStatus(
                                                    s.id
                                                )
                                            }
                                        >
                                            Change
                                        </button>
                                    </td>

                                    <td>
                                        {successStatus[s.id] === "success" && (
                                            <span
                                                style={{
                                                    color: "green",
                                                    fontSize: "20px"
                                                }}
                                            >
                                                ✔
                                            </span>
                                        )}

                                        {successStatus[s.id] === "error" && (
                                            <span
                                                style={{
                                                    color: "red",
                                                    fontSize: "20px"
                                                }}
                                            >
                                                ✖
                                            </span>
                                        )}
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>

                )}
            </>
        );
    };

    const renderTournaments = () => {
        if (!tournaments) return <p>No tournaments found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Tournament</th>
                        <th>Current Status</th>
                        <th>New Status</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {tournaments.map(t => (
                        <tr key={t.id}>
                            <td><b>{t.name}</b> - {t.country} - {t.season}</td>
                            <td>{t.status}</td>
                            <td>
                                <select
                                    value={formData[t.id]?.status || ""}
                                    onChange={(e) => handleTournamentStatusChange(t.id, e.target.value)}
                                >
                                    <option value="">Select...</option>
                                    {tournamentStatuses.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <button onClick={() => changeTournamentStatus(t.id)}>
                                    Change
                                </button>
                            </td>
                            <td>
                                {successStatus[t.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[t.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderResults = () => {
        if (!games) return <p>No games found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Start Time</th>
                        <th>Match</th>
                        <th>Results</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {games.map(game => (
                        <tr key={game.id}>
                            <td>{game.start_time}</td>
                            <td>{game.home} - {game.away}</td>

                            <td>
                                <input
                                    type="text"
                                    placeholder="Home"
                                    value={formData[game.id]?.h || ""}
                                    onChange={(e) => handleResultChange(game.id, "h", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Away"
                                    value={formData[game.id]?.a || ""}
                                    onChange={(e) => handleResultChange(game.id, "a", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Home Halftime"
                                    value={formData[game.id]?.hht || ""}
                                    onChange={(e) => handleResultChange(game.id, "hht", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Away Halftime"
                                    value={formData[game.id]?.aht || ""}
                                    onChange={(e) => handleResultChange(game.id, "aht", e.target.value)}
                                />
                            </td>

                            <td>
                                <button onClick={() => finishGame(game.id)}>Finish</button>
                            </td>
                            <td>
                                {successStatus[game.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[game.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderFixWrongResults = () => {
        if (!games) return <p>No games found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Start Time</th>
                        <th>Match</th>
                        <th>Results</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {games.map(game => (
                        <tr key={game.id}>
                            <td>{game.start_time}</td>
                            <td>{game.home} - {game.away}  {game.home_goals}:{game.away_goals} ({game.home_halftime_goals}:{game.away_halftime_goals})</td>

                            <td>
                                <input
                                    type="text"
                                    placeholder="Home"
                                    value={formData[game.id]?.h || ""}
                                    onChange={(e) => handleResultChange(game.id, "h", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Away"
                                    value={formData[game.id]?.a || ""}
                                    onChange={(e) => handleResultChange(game.id, "a", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Home Halftime"
                                    value={formData[game.id]?.hht || ""}
                                    onChange={(e) => handleResultChange(game.id, "hht", e.target.value)}
                                />
                                <input
                                    type="text"
                                    placeholder="Away Halftime"
                                    value={formData[game.id]?.aht || ""}
                                    onChange={(e) => handleResultChange(game.id, "aht", e.target.value)}
                                />
                            </td>

                            <td>
                                <button onClick={() => finishGame(game.id, 1)}>Finish</button>
                            </td>
                            <td>
                                {successStatus[game.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[game.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderTime = () => {
        if (!games) return <p>No games found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Start Time</th>
                        <th>Match</th>
                        <th>New Start Time</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {games.map(game => (
                        <tr key={game.id}>
                            <td>{game.start_time}</td>
                            <td>{game.home} - {game.away}</td>

                            <td>
                                <input
                                    type="datetime-local"
                                    value={formData[game.id]?.start_time || ""}
                                    onChange={(e) => handleTimeChange(game.id, e.target.value)}
                                />
                            </td>

                            <td>
                                <button onClick={() => changeStartTime(game.id)}>
                                    Change
                                </button>
                            </td>
                            <td>
                                {successStatus[game.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[game.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderLinks = () => {
        if (!links) return <p>No links found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Crawler</th>
                        <th>Url</th>
                        <th>Current Status</th>
                        <th>New Status</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {links.map(l => (
                        <tr key={l.id}>
                            <td>{l.crawler}</td>
                            <td>{l.url}</td>
                            <td>{l.status}</td>
                            <td>
                                <select
                                    value={formData[l.id]?.status || ""}
                                    onChange={(e) => handleLinkStatusChange(l.id, e.target.value)}
                                >
                                    <option value="">Select...</option>
                                    {linkStatuses.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <button onClick={() => changeLinkStatus(l.id)}>
                                    Change
                                </button>
                            </td>
                            <td>
                                {successStatus[l.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[l.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderChances = () => {
        if (!games) return <p>No games found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Start Time</th>
                        <th>Match</th>
                        <th>Chances</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>

                <tbody>
                    {games.map(game => (
                        <tr key={game.id}>

                            <td>{game.start_time}</td>

                            <td>
                                {game.home} - {game.away}
                            </td>

                            <td>
                                <input
                                    type="text"
                                    placeholder="1"
                                    value={getChanceValue(
                                        game,
                                        "1",
                                        "chance1"
                                    )}
                                    onChange={(e) =>
                                        handleResultChange(
                                            game.id,
                                            "chance1",
                                            e.target.value
                                        )
                                    }
                                />

                                <input
                                    type="text"
                                    placeholder="X"
                                    value={getChanceValue(
                                        game,
                                        "X",
                                        "chanceX"
                                    )}
                                    onChange={(e) =>
                                        handleResultChange(
                                            game.id,
                                            "chanceX",
                                            e.target.value
                                        )
                                    }
                                />

                                <input
                                    type="text"
                                    placeholder="2"
                                    value={getChanceValue(
                                        game,
                                        "2",
                                        "chance2"
                                    )}
                                    onChange={(e) =>
                                        handleResultChange(
                                            game.id,
                                            "chance2",
                                            e.target.value
                                        )
                                    }
                                />
                            </td>

                            <td>
                                <button
                                    onClick={() =>
                                        updateChances(game.id)
                                    }
                                >
                                    Update
                                </button>
                            </td>

                            <td>
                                {successStatus[game.id] === "success" && (
                                    <span
                                        style={{
                                            color: "green",
                                            fontSize: "20px"
                                        }}
                                    >
                                        ✔
                                    </span>
                                )}

                                {successStatus[game.id] === "error" && (
                                    <span
                                        style={{
                                            color: "red",
                                            fontSize: "20px"
                                        }}
                                    >
                                        ✖
                                    </span>
                                )}
                            </td>

                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderOldChances = () => {
        if (!games) return <p>No games found</p>;

        return (
            <table className="update-table">
                <thead>
                    <tr>
                        <th>Start Time</th>
                        <th>Match</th>
                        <th>Chances</th>
                        <th>Action</th>
                        <th>Success</th>
                    </tr>
                </thead>
                <tbody>
                    {games.map(game => (
                        <tr key={game.id}>
                            <td>{game.start_time}</td>
                            <td>{game.home} - {game.away}  {game.home_goals}:{game.away_goals} ({game.home_halftime_goals}:{game.away_halftime_goals})</td>

                            <td>
                                <input
                                    type="text"
                                    placeholder="1"
                                    value={getChanceValue(
                                        game,
                                        "1",
                                        "chance1"
                                    )}
                                    onChange={(e) =>
                                        handleResultChange(
                                            game.id,
                                            "chance1",
                                            e.target.value
                                        )
                                    }
                                />

                                <input
                                    type="text"
                                    placeholder="X"
                                    value={getChanceValue(
                                        game,
                                        "X",
                                        "chanceX"
                                    )}
                                    onChange={(e) =>
                                        handleResultChange(
                                            game.id,
                                            "chanceX",
                                            e.target.value
                                        )
                                    }
                                />

                                <input
                                    type="text"
                                    placeholder="2"
                                    value={getChanceValue(
                                        game,
                                        "2",
                                        "chance2"
                                    )}
                                    onChange={(e) =>
                                        handleResultChange(
                                            game.id,
                                            "chance2",
                                            e.target.value
                                        )
                                    }
                                />
                            </td>

                            <td>
                                <button onClick={() => updateChances(game.id)}>Update</button>
                            </td>
                            <td>
                                {successStatus[game.id] === "success" && (
                                    <span style={{ color: "green", fontSize: "20px" }}>✔</span>
                                )}
                                {successStatus[game.id] === "error" && (
                                    <span style={{ color: "red", fontSize: "20px" }}>✖</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <div className="content-container">

            <div className="filter-dropdown-container">
                <select
                    className="filter-dropdown points-dropdown"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                >
                    <option value="seasons">Seasons</option>
                    <option value="tournaments">Tournament status</option>
                    <option value="results">Update results</option>
                    <option value="fix-wrong-results">Fix wrong results</option>
                    <option value="time">Update start time</option>
                    <option value="links">Update links</option>
                    <option value="chances">Update chances</option>
                    <option value="old-chances">Update old chances</option>
                </select>
            </div>

            <div className="user-points dark-main-container scroll-wrapper">
                {mode === "seasons" && renderSeasons()}
                {mode === "tournaments" && renderTournaments()}
                {mode === "results" && renderResults()}
                {mode === "fix-wrong-results" && renderFixWrongResults()}
                {mode === "time" && renderTime()}
                {mode === "links" && renderLinks()}
                {mode === "chances" && renderChances()}
                {mode === "old-chances" && renderOldChances()}
            </div>

        </div>
    );

}

export default SuperAdmin;